import axiosInstance from './AxiosInterceptor';
import { SystemTablesResponse } from '../models/SystemTables';
import { GetOrdersFilters } from '../models/Filters';
import { GetOrdersResponse } from '../models/GetOrdersResponse';
import { GetUsersResponse } from '../models/GetUsersResponse';
import { addBreadcrumb, captureException } from './sentry';
import { ApiError } from '../models/ApiError';
// import { UserOrdersResponse } from '../models/UserOrdersResponse';
// import { GetUnderlingUserOrdersParams } from '../models/GetUnderlingUserOrdersParams';

/**
 * Login API call
 * @param userName - The username of the user
 * @param password - The user's password
 * @param newPassword - Optional new password for NEW_PASSWORD_REQUIRED challenge
 */
export const login = async (userName: string, password: string, newPassword?: string) => {
  addBreadcrumb('Attempting login', 'api', 'info', { userName });
  const response = await axiosInstance.post(`/auth/login`, {
    userName,
    password,
    newPassword,
  });
  addBreadcrumb('Login successful', 'api', 'info', { userName });
  return response.data;
};

/**
 * Logout functionality to clear tokens
 * only used in AxiosInterceptor in the components using AuthContext
 */
export const logout = () => {
  // Clear the tokens from localStorage (or any other storage method)
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userName');

  // Redirect to the login page (full page reload)
  window.location.href = '/'; 
};

/**
 * forgot password API call
 * @param userName - The username of the user
 */
export const forgotPassword = async (userName: string) => {
  const action = 'FORGOT_PASSWORD';
  const response = await axiosInstance.post(`/auth/login`, { 
    userName,
    action      
  });
  return response.data;
};

/**
 * resend confirmation code API call
 * @param userName 
 */
export const resendConfirmationCode = async (userName: string) => {
  const action = 'RESEND_CONFIRMATION_CODE';
  const response = await axiosInstance.post(`/auth/login`, { 
    userName,
    action
  });
  return response.data;
};

export const confirmResetRequest = async (userName: string, confirmationCode: string, newPassword: string) => {
  const action = 'CONFIRM_FORGOT_PASSWORD'
  const response = await axiosInstance.post(`/auth/login`, {
    userName,
    confirmationCode,
    newPassword,
    action
  });
  return response.data;
};

/**
 * Fetch system tables for the logged-in user.
 */
export const getSystemTables = async (): Promise<SystemTablesResponse> => {
  const response = await axiosInstance.get<SystemTablesResponse>('/system/tables');
  return response.data;
};

/**
 * Fetch underlings orders for the logged-in user.
 */
export const getOrders = async (params?: GetOrdersFilters): Promise<GetOrdersResponse> => {
  // build a `query` object for the GET request
  const queryParams: Record<string, string | number | boolean | undefined> = {
    clientInnerId: params?.clientInnerId,
    startDate: params?.startDate,
    endDate: params?.endDate,
    dateSearchType: params?.dateSearchType,
    shift: params?.shift,
    orderId: params?.orderId,
    status: params?.status,
    origin: params?.origin,
    isSelfShipping: params?.isSelfShipping,
    transportationCompany: params?.transportationCompany,
    page: params?.page,
    limit: params?.limit,
  };

  // Filter out undefined keys
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] === undefined) {
      delete queryParams[key];
    }
  });

  const response = await axiosInstance.get<GetOrdersResponse>('/orders', {
    params: queryParams,
  });
  const data = response.data;
  data.orders.forEach((o) => {
    if (o.orderCreationTime) {
      o.orderCreationTime = parseDate(o.orderCreationTime);
    }
    if (o.requestedTime) {
      o.requestedTime = parseDate(o.requestedTime);
    }
    if (o.expectedReceiveTime) {
      o.expectedReceiveTime = parseDate(o.expectedReceiveTime);
    }
  });

  return data;
};

/** 
 * Utility that tries to parse a string into a Date, 
 * returning undefined if invalid or empty.
 */
function parseDate(dateString: string | Date): Date | undefined {
  if (!dateString || typeof dateString !== 'string') return undefined;
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? undefined : d;
}

export const createOrder = async (orderData: {
  itemNum: string;
  origin: string;
  amount: string | number;
  isSelfShipping: boolean;
  requestedTime: string;
  clientInnerId: string;
  transportationCompany?: string;
  underlingUserName?: string;
  tonAmount?: string | number;
  comment?: string;
}) => {
  // POST to the correct endpoint for creating orders
  const response = await axiosInstance.post(`/orders`, orderData);
  return response.data;
};

export const updateOrder = async (orderData: {
  orderId: string;
  itemNum: string;
  clientInnerId: string;
  origin?: string | number;
  amount?: string | number;
  isSelfShipping?: boolean;
  requestedTime?: string;
  transportationCompany?: string;
  tonAmount?: string | number;
  comment?: string;
}) => {
  // Convert string values to numbers for amount and tonAmount
  // Convert origin to number if it's a string
  const processedOrderData: any = {
    orderId: orderData.orderId,
    itemNum: orderData.itemNum,
    clientInnerId: orderData.clientInnerId,
  };

  // Add optional fields if they exist
  if (orderData.origin !== undefined) {
    processedOrderData.origin = typeof orderData.origin === 'string' ? parseInt(orderData.origin, 10) : orderData.origin;
  }
  if (orderData.amount !== undefined) {
    processedOrderData.amount = typeof orderData.amount === 'string' ? parseFloat(orderData.amount) || 0 : orderData.amount;
  }
  if (orderData.isSelfShipping !== undefined) {
    processedOrderData.isSelfShipping = orderData.isSelfShipping;
  }
  if (orderData.requestedTime) {
    // Convert datetime-local format to ISO string
    const date = new Date(orderData.requestedTime);
    processedOrderData.requestedTime = date.toISOString();
  }
  if (orderData.transportationCompany !== undefined) {
    processedOrderData.transportationCompany = orderData.transportationCompany;
  }
  if (orderData.tonAmount !== undefined) {
    processedOrderData.tonAmount = typeof orderData.tonAmount === 'string' ? parseFloat(orderData.tonAmount) || 0 : orderData.tonAmount;
  }
  if (orderData.comment !== undefined) {
    processedOrderData.comment = orderData.comment;
  }

  // PUT to the correct endpoint for updating orders
  const response = await axiosInstance.put(`/orders`, processedOrderData);
  return response.data;
};

export async function approveOrder(orderId: string) {
  const response = await axiosInstance.post('/orders/update', { orderId });
  return response.data; // Expected to contain something like { newStatusId: number }
}

export async function rejectOrder(orderId: string) {
  const response = await axiosInstance.post('/orders/rejectOrder', { orderId });
  return response.data; // Expected to contain something like { newStatusId: number }
}

export async function updateTransportCompany(orderId: string, transportationCompany: string) {
  const response = await axiosInstance.post('/orders/updateShippingCompany', { orderId, transportationCompany });
  return response.data; // e.g., { updatedTransport: string }
}

export async function getClientsInnerId() {
  const response = await axiosInstance.get('/users/getPriority');
  return response.data;
}

export const createUser = async (userData: {
  username: string;
  phone_number: string;
  email: string | number;
  clientInnerId: string;
  'custom:role': string;
  'custom:app': string;
}) => {
  // POST to the correct endpoint for creating users
  const response = await axiosInstance.post(`/users/create`, userData);
  return response.data;
};

export const getUsers = async (): Promise<GetUsersResponse> => {
  try {
    addBreadcrumb('Fetching users', 'api', 'info');
    const response = await axiosInstance.get<GetUsersResponse>('/users/getAll', {});
    addBreadcrumb('Users fetched successfully', 'api', 'info');
    return response.data;
  } catch (error) {
    return handleApiError(error, 'getUsers');
  }
};

export const updateUser = async (username: string, updates: Record<string, string>) => {
  try {
    addBreadcrumb('Updating user', 'api', 'info', { username });
    const response = await axiosInstance.post('/users/update', {
      username,
      updates
    });
    addBreadcrumb('User updated successfully', 'api', 'info', { username });
    return response.data;
  } catch (error) {
    handleApiError(error, 'updateUser');
  }
};

export const deleteUser = async (username: string) => {
  try {
    addBreadcrumb('Deleting user', 'api', 'info', { username });
    const response = await axiosInstance.delete('/users', {
      data: { username }
    });
    addBreadcrumb('User deleted successfully', 'api', 'info', { username });
    return response.data;
  } catch (error) {
    handleApiError(error, 'deleteUser');
  }
};

export interface ClientReportResponse {
  message: string;
  files: {
    clientKey: string;
    clientName: string;
    fileName: string;
    url: string;
  }[];
}

export const generateClientReport = async (params: {
  startDate: string;
  endDate: string;
  customerName: string;
}): Promise<ClientReportResponse> => {
  const response = await axiosInstance.post<ClientReportResponse>('/reports', params);
  return response.data;
};

export interface SiloDelivery {
  _id: string;
  phoneNumber: string;
  entranceImageUrl: string;
  entranceTimestamp: Date;
  status: string;
  deliveries: {
    deliveryId: string;
    siloNumber: string;
    startImageUrl: string;
    startTimestamp: Date;
    status: string;
    createdAt: Date;
    updatedAt: Date;
    endImageUrl: string;
    endTimestamp: Date;
  }[];
  createdAt: Date;
  updatedAt: Date;
  portVisitNumber: string;
  exitImageUrl?: string;
  exitTimestamp?: Date;
}

export interface GetSiloDeliveriesResponse {
  data: SiloDelivery[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const getSiloDeliveries = async (params?: {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  phoneNumber?: string;
  status?: string;
}): Promise<GetSiloDeliveriesResponse> => {
  try {
    addBreadcrumb('Fetching silo deliveries', 'api', 'info');
    
    // Build query parameters
    const queryParams: Record<string, string | number | undefined> = {
      page: params?.page,
      limit: params?.limit,
      startDate: params?.startDate,
      endDate: params?.endDate,
      phoneNumber: params?.phoneNumber,
      status: params?.status,
    };

    // Filter out undefined keys
    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });

    const response = await axiosInstance.get<GetSiloDeliveriesResponse>('/port-visits', {
      params: queryParams,
    });
    
    addBreadcrumb('Silo deliveries fetched successfully', 'api', 'info');
    return response.data;
  } catch (error) {
    handleApiError(error, 'getSiloDeliveries');
    throw error; // Re-throw the error after logging it
  }
};

export interface DeliveryCertificate {
  _id: string;
  shippingCertificateNumber: string;
  shippingCertificateId: string;
  clientName: string;
  date: string;
  siteOfOrigin: string;
  destination: string;
  loadingTime: string;
  transportationCompany: string;
  driverName: string;
  cartNote: string | null;
  truckNum: string;
  productId: string;
  itemDescription: string;
  manufacturer: string;
  destinationReceiverName: string;
  comments: string;
  createdAt: string;
  createdBy: string;
  userEmail: string;
  driverUploadedFiles?: Array<{
    presignedUrl: string;
  }>;
  taskYamIntegration?: {
    success: boolean;
    error?: {
      message: string;
    };
    skipped?: boolean;
  };
}

export interface DeliveryCertificatesResponse {
  shipments: DeliveryCertificate[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  startDate: string;
  endDate: string;
}

export const getDeliveryCertificates = async (params: {
  startDate?: string;
  endDate?: string;
  clientName?: string;
  productId?: string;
  onlyWithImages?: boolean;
  page?: number;
  limit?: number;
}): Promise<DeliveryCertificatesResponse> => {
  try {
    addBreadcrumb('Fetching delivery certificates', 'api', 'info');
    
    // Build query parameters
    const queryParams: Record<string, string | number | boolean | undefined> = {
      startDate: params.startDate,
      endDate: params.endDate,
      clientName: params.clientName,
      productId: params.productId,
      onlyWithImages: params.onlyWithImages,
      page: params.page,
      limit: params.limit,
    };

    // Filter out undefined keys
    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });

    const response = await axiosInstance.get<DeliveryCertificatesResponse>('/shipments', {
      params: queryParams,
    });
    
    addBreadcrumb('Delivery certificates fetched successfully', 'api', 'info');
    return response.data;
  } catch (error) {
    handleApiError(error, 'getDeliveryCertificates');
    throw error;
  }
};

/**
 * Helper function to handle API errors with Sentry logging
 * Errors are already converted to ApiError by the interceptor
 */
const handleApiError = (error: any, context: string): never => {
  const apiError = ApiError.isApiError(error) ? error : ApiError.fromUnknownError(error);
  
  const errorToLog = error instanceof Error ? error : new Error(apiError.message);
  errorToLog.name = `ApiError: ${apiError.type}`;
  
  captureException(errorToLog, {
    tags: {
      source: 'ApiService',
      context,
      errorType: apiError.type,
      statusCode: apiError.statusCode?.toString() || 'unknown',
      requestId: apiError.requestId || 'none',
    },
    extra: {
      apiError: {
        type: apiError.type,
        message: apiError.message,
        details: apiError.details,
        timestamp: apiError.timestamp,
        requestId: apiError.requestId,
        statusCode: apiError.statusCode,
      },
    },
  });
  
  throw apiError;
};